<template>
    <div class="assignment-list sidebar-list-wrapper">
        <!-- This is for screen readers -->
        <div class="sr-only" aria-live="assertive" aria-atomic="true">
            {{ searchStatus }}
        </div>

        <div class="sidebar-filter">
            <b-form-group
                id="filter-assignments"
                label="Filter assignments"
                label-for="filter-assignments-input"
            >
                <input
                    id="filter-assignments-input"
                    class="form-control"
                    :value="filter"
                    v-debounce="
                        newFilter => {
                            filter = newFilter;
                        }
                    "
                    ref="filter"
                />
            </b-form-group>
        </div>

        <ul class="sidebar-list" v-if="assignments.length > 0 || loading > 0">
            <template v-if="currentAssignment.isJust()">
                <li class="sidebar-list-section-header text-muted">
                    <small>Current assignment</small>
                </li>
                <assignment-list-item
                    :key="`current-assignment-${currentAssignment.extract().id}`"
                    :current-id="currentAssignment.extract().id"
                    no-popover
                    :sbloc="sbloc"
                    :show-course-name="currentCourse == null"
                    :assignment="currentAssignment.extract()"
                />
                <li>
                    <hr class="separator" />
                </li>
            </template>
            <li v-if="loading > 0" class="loader-item">
                <cg-loader :scale="1" />
            </li>
            <template v-else>
                <li class="sidebar-list-section-header text-muted" v-if="showTopAssignments">
                    <small>Closest deadlines</small>
                </li>

                <assignment-list-item
                    v-for="assignment in topAssignments"
                    small
                    :key="`top-assignment-${assignment.id}`"
                    v-if="showTopAssignments"
                    :current-id="currentAssignment.mapOrDefault(a => a.id, null)"
                    no-popover
                    :sbloc="sbloc"
                    :show-course-name="currentCourse == null"
                    :assignment="assignment"
                />

                <li v-if="showTopAssignments">
                    <hr class="separator" />
                </li>

                <li
                    v-if="filteredAssignments.length === 0"
                    class="text-center text-muted font-italic"
                    role="status"
                >
                    No matching assignments found
                </li>

                <assignment-list-item
                    v-for="assignment in filteredAssignments.slice(0, visibleAssignments)"
                    :key="`sorted-assignment-${assignment.id}`"
                    :assignment="assignment"
                    no-popover
                    :current-id="currentAssignment.mapOrDefault(a => a.id, null)"
                    :show-course-name="currentCourse == null"
                    :sbloc="sbloc"
                />

                <li class="d-flex mx-2 my-1" v-if="moreAssignmentsAvailable">
                    <b-btn class="flex-grow" @click="showMoreAssignments()">
                        <cg-loader v-if="renderingMoreAssignments > 0" :scale="1" class="py-1" />
                        <span v-else v-b-visible="visible => visible && showMoreAssignments()">
                            Load more assignments
                        </span>
                        <infinite-loading @infinite="showMoreAssignments" :distance="150">
                            <div slot="spinner"></div>
                            <div slot="no-more"></div>
                            <div slot="no-results"></div>
                            <div slot="error" slot-scope="err">
                                {{ err }}
                            </div>
                        </infinite-loading>
                    </b-btn>
                </li>
            </template>
        </ul>
        <span v-else class="sidebar-list no-items-text"> You don't have any assignments yet. </span>

        <hr class="separator" v-if="showAddButton || showManageButton" />

        <div class="sidebar-footer">
            <button-with-popover-controls
                v-if="showAddButton"
                :id="addButtonId"
                class="add-assignment-button sidebar-footer-button"
                :description="addAssignmentPopover"
                :popover-props="{
                    id: popoverId,
                    triggers: 'click',
                    placement: 'bottom',
                    boundary: 'window',
                    boundaryPadding: 16,
                    customClass: 'no-max-width',
                }"
            >
                <Icon slot="buttonContent" :icon="icons.Plus" />

                <submit-input
                    slot="controlsContent"
                    style="width: 18rem"
                    :confirm="addAssignmentConfirm"
                    placeholder="New assignment name"
                    @create="createNewAssignment"
                    @cancel="closePopover"
                />
            </button-with-popover-controls>

            <router-link
                v-if="showManageButton"
                class="btn sidebar-footer-button"
                :class="{ active: manageButtonActive }"
                v-b-popover.hover.focus.top="'Manage course'"
                :to="manageCourseRoute(currentCourse.id)"
            >
                <Icon :icon="icons.Settings" />
            </router-link>
        </div>
    </div>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading';

import { Plus, Settings } from '@codegrade/icons';

import ButtonWithPopoverControls from '@/components/ButtonWithPopoverControls';
import { Icon, mapIcons } from '@/components/Icon';
import * as models from '@/models';
import { AssignmentsStore, CoursesStore } from '@/store';
import * as utils from '@/utils';

import SubmitInput from '../SubmitInput';
import AssignmentListItem from './AssignmentListItem';

const EXTRA_ASSIGNMENTS = 15;
const TOP_ASSIGNMENTS_LENGTH = 7;

export default {
    name: 'assignment-list',

    props: {
        data: {
            type: Object,
            default: null,
        },
    },

    computed: {
        ...mapIcons({ Plus, Settings }),

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        sbloc() {
            return this.currentCourse ? undefined : 'a';
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        currentAssignment() {
            return AssignmentsStore.getAssignment()(this.$route.params.assignmentId);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        currentCourse() {
            if (this.data && this.data.course) {
                return CoursesStore.getCourse()(this.data.course.id).extract();
            } else {
                return null;
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        showAddButton() {
            const course = this.currentCourse;
            return !!(this.loading === 0 && course && course.canCreateAssignments);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        showManageButton() {
            const course = this.currentCourse;
            return !!(this.loading === 0 && course && course.canManage);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        manageButtonActive() {
            const course = this.currentCourse;
            return !!(
                this.$route.name === 'manage_course' &&
                course &&
                this.$route.params.courseId.toString() === course.id.toString()
            );
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        assignments() {
            return this.currentCourse
                ? this.currentCourse.assignments
                : AssignmentsStore.allAssignments();
        },

        /** @returns The list of assignments to be displayed at the top of the sidebar. */
        topAssignments() {
            const lookup = this.assignments.reduce((res, cur) => {
                cur.deadline.ifJust(deadline => {
                    res[cur.id] = Math.abs(deadline.diff(this.$getRootInstance().$now));
                });
                return res;
            }, {});

            return this.assignments
                .filter(a => lookup[a.id] != null)
                .sort((a, b) => lookup[a.id] - lookup[b.id])
                .slice(0, TOP_ASSIGNMENTS_LENGTH);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        showTopAssignments() {
            if (this.filter) {
                return false;
            }
            return this.assignments.length >= TOP_ASSIGNMENTS_LENGTH + 2;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        sortedAssignments() {
            return models.Assignment.sortAssignments(this.assignments);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        filteredAssignments() {
            if (!this.filter) {
                return this.sortedAssignments;
            }

            const filterParts = this.filter.toLocaleLowerCase().split(' ');

            return this.sortedAssignments.filter(assig =>
                filterParts.every(
                    part =>
                        assig.name.toLocaleLowerCase().indexOf(part) > -1 ||
                        assig.course.name.toLocaleLowerCase().indexOf(part) > -1,
                ),
            );
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        addAssignmentPopover() {
            const course = this.currentCourse;
            if (!course || !course.isLTI) {
                return 'Add a new assignment';
            }
            return 'Add a new assignment that is not connected to your LMS.';
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        addAssignmentConfirm() {
            if (!this.currentCourse) {
                return '';
            }
            return this.currentCourse.ltiProvider.mapOrDefault(prov => {
                const lms = prov.lms;
                return `You are about to create an assignment that is not connected to ${lms}. This means that students will not be able to navigate to this assignment inside ${lms} and grades will not be synced. Are you sure?`;
            }, '');
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        moreAssignmentsAvailable() {
            return this.visibleAssignments <= this.assignments.length;
        },
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    async mounted() {
        this.$root.$on('sidebar::reload', this.reloadAssignments);

        if (this.currentCourse == null) {
            await this.asLoader(CoursesStore.loadAllCourses());
        }

        await this.$nextTick();
        const activeEl = document.activeElement;
        if (
            !activeEl ||
            !activeEl.matches('input, textarea') ||
            activeEl.closest('.sidebar .submenu')
        ) {
            this.$withRef('filter', filter => filter.focus());
        }
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        const id = utils.getUniqueId();
        return {
            filter: '',
            loading: 0,
            renderingMoreAssignments: 0,
            visibleAssignments: EXTRA_ASSIGNMENTS * 4,
            addButtonId: `assignment-add-btn-${id}`,
            popoverId: `assignment-add-popover-${id}`,
            searchStatus: '',
        };
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    destroyed() {
        this.$root.$off('sidebar::reload', this.reloadAssignments);
    },

    watch: {
        /**
         * A watcher which checks the current list of assignments and provides
         * an appropriate status message.
         *
         * @param assignments The list of assignments to filter.
         */
        filteredAssignments(assignments) {
            if (assignments.length === 0) {
                this.searchStatus = 'No matching assignments found. Clear field and try again.';
            } else {
                this.searchStatus = '';
            }
        },
    },

    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async asLoader(promise) {
            if (this.loading === 0) {
                this.$emit('loading');
            }

            this.loading += 1;
            await promise;
            this.loading = Math.max(0, this.loading - 1);

            if (this.loading === 0) {
                this.$emit('loaded');
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        reloadAssignments() {
            if (this.currentCourse) {
                return this.asLoader(utils.waitAtLeast(250, Promise.resolve()));
            }
            return this.asLoader(CoursesStore.reloadCourses({ fullReload: true }));
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        manageCourseRoute(courseId) {
            return {
                name: 'manage_course',
                params: { courseId },
            };
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        createNewAssignment(name, resolve, reject) {
            AssignmentsStore.createAssignment({
                courseId: this.currentCourse.id,
                name,
            }).then(res => {
                resolve(
                    res.addCallbacks({
                        onAfterSuccess: () => {
                            this.closePopover();
                            this.$router.push({
                                name: 'manage_assignment',
                                params: {
                                    courseId: this.currentCourse.id.toString(10),
                                    assignmentId: res.data.id.toString(10),
                                },
                            });
                        },
                    }),
                );
            }, reject);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        closePopover() {
            this.$root.$emit('bv::hide::popover', this.popoverId);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async showMoreAssignments(state = null) {
            this.renderingMoreAssignments += 1;
            await this.$afterRerender();
            this.visibleAssignments += EXTRA_ASSIGNMENTS;
            this.renderingMoreAssignments -= 1;
            if (state) {
                if (this.moreAssignmentsAvailable) {
                    state.loaded();
                } else {
                    state.complete();
                }
            }
        },
    },

    components: {
        Icon,
        AssignmentListItem,
        SubmitInput,
        InfiniteLoading,
        ButtonWithPopoverControls,
    },
};
</script>
